/**
 * 🐋 Advertiser Auth Component - Cetalingua FM
 * Login for whale watching companies with existing accounts
 * (New advertisers go through landing page checkout flow)
 */

import React, { useState, useEffect } from 'react';
import PasswordInput from './common/PasswordInput';
import ForgotPassword from './common/ForgotPassword';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase-config';
import { getUserRole } from '../utils/roleManager';
import './AdvertiserAuth.css';
import { preloadRecaptcha, getRecaptchaToken } from '../utils/recaptcha';
import portalIcon from '../assets/portal-icon.png';

const AdvertiserAuth = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { preloadRecaptcha(); }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await getRecaptchaToken('advertiser_login'); // invisible security check (reCAPTCHA v3)
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Check if user is an advertiser
      const role = await getUserRole(user.uid);

      if (role !== 'advertiser' && role !== 'station_advertiser') {
        setError('This account is not registered as an advertiser. Please use the admin login or sign up as a new advertiser.');
        await auth.signOut();
        return;
      }

      // Success! User will be redirected by App.js
      console.log('🐋 Advertiser logged in successfully');
      if (onSuccess) onSuccess();

    } catch (err) {
      console.error('Login error:', err);
      
      switch (err.code) {
        case 'auth/user-not-found':
          setError('No account found with this email. Please sign up first.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        case 'auth/invalid-credential':
          setError('Invalid email or password. Please try again.');
          break;
        default:
          setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="advertiser-auth-container">
      <div className="advertiser-auth-card">
        <div className="advertiser-auth-header">
          <img src={portalIcon} alt="Cetalingua portal" style={{ width: 80, height: 80, borderRadius: 18 }} />
          <h1>Cetalingua FM</h1>
          <h2>Advertiser / Whale Watching Company</h2>
          <p>Welcome back! Sign in to manage your listings.</p>
        </div>

        <form onSubmit={handleLogin} className="advertiser-auth-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <ForgotPassword email={email} />

        <div className="advertiser-auth-footer">
          <p className="new-advertiser-text">
            New to Cetalingua FM?
          </p>
          <div className="signup-options">
            <a href="/advertise" className="signup-link">
              📢 Become an Advertiser
            </a>
            <a href="/station" className="signup-link">
              📡 Host a Station
            </a>
          </div>
          <p className="login-note">
            Advertisers, Station Operators, and Combined Partners all login here
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdvertiserAuth;
